
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Bot, SendHorizonal, Sparkles, Link, ClipboardCopy, Check, AlertCircle } from 'lucide-react';
import { useAIAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import { openAiService } from '@/services/ai';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { applyMarkdownFormatting } from '@/utils/textFormatting';

interface AskAIDialogProps {
  articleId: string;
}

const AskAIDialog: React.FC<AskAIDialogProps> = ({ articleId }) => {
  const { setIsOpen, sendMessage } = useAIAssistant();
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState('');
  const [recentQuestions] = useState([
    "Can you summarize the key points?",
    "What are the challenges for renewable energy in cities?",
    "How does waste-to-energy work?",
    "Which cities are leading in urban renewable energy?"
  ]);
  const [isCopied, setIsCopied] = useState(false);
  
  const handleSendQuestion = async () => {
    if (!question.trim() || isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Sending question about article #${articleId}: ${question}`);
      const result = await openAiService.chatWithAI(
        `About article #${articleId}: ${question}`, 
        'balanced'
      );
      
      console.log('AI response received:', result);
      
      if (result.success && result.data) {
        setResponse(result.data.response || "I'm sorry, I couldn't process that request.");
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error getting article AI response:', error);
      setError(typeof error === 'string' ? error : error instanceof Error ? error.message : 'An unknown error occurred');
      toast.error('Sorry, there was a problem getting a response');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRecentQuestion = (q: string) => {
    setQuestion(q);
    setResponse('');
    setError(null);
  };

  const handleOpenFullAssistant = () => {
    // If there's a question, send it to the full assistant
    if (question.trim()) {
      sendMessage(question);
    }
    
    // Close the dialog and open the assistant
    setIsOpen(true);
  };

  const handleCopyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(response)
        .then(() => {
          setIsCopied(true);
          toast.success('Response copied to clipboard');
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch(() => {
          toast.error('Failed to copy to clipboard');
        });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" className="gap-2">
          <Bot className="h-4 w-4" />
          Ask AI about this article
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="sr-only">Ask AI about this article</DialogTitle>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-bold mb-1">Ask AI about this article</h3>
            <p className="text-sm text-muted-foreground">
              Our AI can answer your questions about the content in this article
            </p>
          </div>
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}
          
          {!response ? (
            <>
              <div>
                <label className="text-sm font-medium mb-2 block">Your question</label>
                <Textarea 
                  placeholder="Ask anything about this article..." 
                  value={question} 
                  onChange={(e) => setQuestion(e.target.value)}
                  className="mb-2 resize-none"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button 
                    className="flex-1" 
                    onClick={handleSendQuestion}
                    disabled={!question.trim() || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Sparkles className="mr-2 h-4 w-4 animate-spin" />
                        Thinking...
                      </>
                    ) : (
                      <>
                        <SendHorizonal className="mr-2 h-4 w-4" />
                        Send Question
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleOpenFullAssistant}
                  >
                    <Bot className="mr-2 h-4 w-4" />
                    Open Full Assistant
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Or try one of these</label>
                <div className="flex flex-wrap gap-2">
                  {recentQuestions.map((q, index) => (
                    <Button 
                      key={index} 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRecentQuestion(q)}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div className="bg-accent/30 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium mb-2">Your question:</p>
                <p className="text-sm">{question}</p>
              </div>
              
              <div className="bg-primary/10 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="h-5 w-5 text-primary" />
                  <p className="text-sm font-medium">AI response:</p>
                </div>
                <div 
                  className="text-sm"
                  dangerouslySetInnerHTML={{ 
                    __html: applyMarkdownFormatting(response) 
                  }}
                />
                <div className="mt-3 pt-3 border-t border-primary/10 flex items-center gap-1 text-xs text-muted-foreground">
                  <Link className="h-3 w-3" />
                  Sources: <a href="/article/1" className="underline">Urban Renewable Energy</a>, <a href="/article/2" className="underline">Smart City Planning</a>
                </div>
              </div>
              
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setResponse('');
                    setError(null);
                  }}
                >
                  Ask another question
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={handleOpenFullAssistant}>
                    <Bot className="mr-2 h-3 w-3" />
                    Continue in Full Assistant
                  </Button>
                  <Button variant="default" size="sm" onClick={handleCopyResponse}>
                    {isCopied ? (
                      <>
                        <Check className="mr-2 h-3 w-3" />
                        Copied
                      </>
                    ) : (
                      <>
                        <ClipboardCopy className="mr-2 h-3 w-3" />
                        Copy response
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AskAIDialog;
